import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, ExternalLink } from 'lucide-react';
import moment from 'moment';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useOwnershipClaimQuery } from '@/client/api/ownership-claims/ownership-claims.query';
import type { ClaimAction, OwnershipClaim } from '@/client/types/ownership-claims';
import ClaimAttachmentPreview from './ClaimAttachmentPreview';
import ClaimActionDialog from './ClaimActionDialog';
import ClaimNotes from './ClaimNotes';
import { CLAIM_STATUS_BADGE, availableActions, personLabel } from './claimStatus';

const ACTION_LABEL: Record<ClaimAction, string> = {
  approve: 'Approve',
  reject: 'Reject',
  reopen: 'Reopen',
  remove: 'Remove',
};

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="break-words text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const Person = ({ person }: { person?: OwnershipClaim['claimer'] }) =>
  person ? (
    <Link to={`/admin/users/${person.uid}`} className="text-blue-600 hover:underline">
      {personLabel(person)}
      {person.email && person.name ? <span className="block text-xs font-normal text-gray-500">{person.email}</span> : null}
    </Link>
  ) : (
    <>—</>
  );

interface ClaimReviewDrawerProps {
  /** Row the drawer was opened from — shown until the full detail loads. */
  summary: OwnershipClaim | null;
  onClose: () => void;
}

const ClaimReviewDrawer = ({ summary, onClose }: ClaimReviewDrawerProps) => {
  const [action, setAction] = useState<ClaimAction | null>(null);
  const { data: detail, isLoading } = useOwnershipClaimQuery(summary?.claimid ?? null);
  const claim = detail ?? summary;
  if (!claim) return null;

  const attachments = claim.attachments ?? [];
  const actions = availableActions(claim);

  return (
    <>
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
          <SheetHeader className="border-b border-gray-100 p-4 pr-12 text-left">
            <SheetTitle className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-blue-700" />
              {claim.entity?.name || `Listing #${claim.eid}`}
            </SheetTitle>
            <SheetDescription>Ownership claim #{claim.claimid}</SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-6 overflow-y-auto p-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoRow
                label="Status"
                value={
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${CLAIM_STATUS_BADGE[claim.status]}`}>
                    {claim.status}
                  </span>
                }
              />
              <InfoRow
                label="Submitted"
                value={claim.date_added ? moment(claim.date_added).format('YYYY-MM-DD HH:mm') : '—'}
              />
              <InfoRow label="Claimant" value={<Person person={claim.claimer} />} />
              <InfoRow label="Owner when claimed" value={<Person person={claim.owner} />} />
              <InfoRow
                label="Listing"
                value={
                  <Link to={`/admin/listings/${claim.eid}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                    eid {claim.eid} <ExternalLink className="h-3 w-3" />
                  </Link>
                }
              />
              <InfoRow
                label="If approved, moves to"
                value={`${personLabel(claim.claimer)}'s personal account`}
              />
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-gray-500">Why the claimant says it's theirs</p>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                {claim.description || '—'}
              </p>
            </div>

            {claim.reviewed_at && (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">Latest decision</p>
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                  <span className="font-medium capitalize">{claim.status}</span> by{' '}
                  {claim.reviewed_by === 'admin' ? `admin ${personLabel(claim.reviewer)}` : 'the listing owner'} ·{' '}
                  {moment(claim.reviewed_at).format('YYYY-MM-DD HH:mm')}
                  {claim.review_note ? <span className="mt-1 block text-gray-600">“{claim.review_note}”</span> : null}
                </p>
              </div>
            )}

            <div>
              <p className="mb-2 text-sm font-semibold text-gray-900">Supporting documents ({attachments.length})</p>
              {isLoading && !detail ? (
                <Skeleton className="h-40 w-full" />
              ) : attachments.length === 0 ? (
                <p className="text-sm text-gray-500">No documents on this claim.</p>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {attachments.map((a) => (
                    <ClaimAttachmentPreview key={a.asid} attachment={a} />
                  ))}
                </div>
              )}
            </div>

            <ClaimNotes claimid={claim.claimid} notes={detail?.notes ?? []} />
          </div>

          {actions.length > 0 && (
            <SheetFooter className="flex-row justify-end gap-2 border-t border-gray-100 p-4 sm:space-x-0">
              {actions.map((a) => (
                <PermissionButton
                  key={a}
                  code="claim.review"
                  variant={a === 'approve' || a === 'reopen' ? 'default' : a === 'remove' ? 'ghost' : 'destructive'}
                  onClick={() => setAction(a)}
                >
                  {ACTION_LABEL[a]}
                </PermissionButton>
              ))}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {action && <ClaimActionDialog claim={claim} action={action} onClose={() => setAction(null)} />}
    </>
  );
};

export default ClaimReviewDrawer;
